import { action, observable, makeObservable, toJS, runInAction } from 'mobx';
import { destroy, patch, post } from 'stores/_services/dataService';
import { expressionToString } from 'stores/_services/scheduleManagementService';
import log from 'services/helpers/log';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getLocalTimeZone } from '../../helpers/getLocalTimeZone';
dayjs.extend(utc);

const DEFAULT_SCHEDULE = '0 9 * * 3';

interface IScanOptions {
    process: boolean;
    module: boolean;
    driver: boolean;
    memory: boolean;
    account: boolean;
    artifact: boolean;
    autostart: boolean;
    application: boolean;
    network: boolean;
    extensions: {
        id: string;
        args?: any;
        order?: number;
    };
}

export interface ITargetGroup {
    id: string | null;
    name: string;
    displayName?: string;
    deleted: boolean;
    totalAddressCount: number;
    accessibleAddressCount: number;
    reachableAddressCount: number;
    activeAgentCount: number;
    lastScannedOn: Date | null;
    boxes: [];
    boxCount: number;
    data?: {
        isDefault?: boolean;
        rmmUid?: boolean;
        vsaId?: boolean;
    };
}

export default class TargetGroupStore {
    targetGroup: ITargetGroup = {
        id: null,
        name: '',
        deleted: false,
        totalAddressCount: 0,
        accessibleAddressCount: 0,
        reachableAddressCount: 0,
        activeAgentCount: 0,
        lastScannedOn: null,
        boxes: [],
        boxCount: 0,
        data: {}
    };

    constructor(targetGroup: ITargetGroup) {
        makeObservable(this, {
            targetGroup: observable,
            update: action,
            rename: action,
            delete: action,
            enumerate: action,
            _updateTarget: action
        });

        this.update(targetGroup);
    }

    update(targetGroup: Partial<ITargetGroup> = {}): void {
        this.targetGroup = {
            ...this.targetGroup,
            ...targetGroup,
            displayName: `${targetGroup.name}${targetGroup.deleted ? ' (deleted)' : ''}`
        };
    }

    async rename(name: string): Promise<void> {
        await this._updateTarget({ name });
    }

    async delete(): Promise<void> {
        await destroy(`targets/${this.targetGroup.id}`);
    }

    async enumerate({ queries }: { queries: unknown[] }): Promise<void> {
        await post(`targets/${this.targetGroup.id}/enumerate`, { queries });
    }

    async _updateTarget(payload: Partial<ITargetGroup>): Promise<void> {
        try {
            // eslint-disable-next-line no-var
            var { data } = await patch(`targets/${this.targetGroup.id}`, payload);
        } catch (e) {
            log.error(e as Error);
        }
        if (data) {
            runInAction(() => {
                this.targetGroup = data;
            });
        }
    }
}
